import React, { useMemo, useState } from "react"
import moment from "moment"
import Grid from "@mui/material/Grid"
import { HotelContractModel, HotelRoomTypeModel, RoomSpecificDifferentSurchargeModel, SeasonModel } from "../../models"
import Select from "../select/select"
import IconsColor from "../../util/iconsColor"
import styles from "./ratesOverview.module.scss"
import {
    getDayWiseSurchargeDisplayRows,
    getSeasonalExtraBedDisplayRows,
    shouldHideAdultExtraBedInSurcharge,
} from "./surchargeDisplayUtils"
import { GitFitType } from "./hotelRates"
import { toTitleCase } from "util/textcase"

/* ─────────────────────────── constants ─────────────────────────── */

const RATE_STRUCTURE_NAMES: Record<number, string> = {
    1: "Simple",
    2: "Meal Plan",
    3: "GIT/FIT",
}

/* ─────────────────────────── helpers ───────────────────────────── */

const formatVND = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return "—"
    return value.toString()
}

const getSurchargeAmountColumnHeader = (
    rows: { value?: number | null; isPercentage?: boolean }[],
): string => {
    const definedRows = rows.filter((row) => row.value != null)
    if (definedRows.length === 0) return "Amount (VND / Night)"
    if (definedRows.every((row) => row.isPercentage === true)) return "Amount (% / Night)"
    if (definedRows.every((row) => row.isPercentage !== true)) return "Amount (VND / Night)"
    return "Amount"
}

const roomLabel = (name: string | null, bedType: string | null): string => {
    const n = (name ?? "").trim()
    const b = (bedType ?? "").trim()
    return b ? `${n} (${b})` : n
}

const sameMealPlanId = (a: number | null | undefined, b: number | null | undefined) =>
    (a == null ? null : Number(a)) === (b == null ? null : Number(b))

/** Format season date range for display (e.g. "01 Feb - 28 Feb" or "01 Feb - 15 Feb, 20 Feb - 28 Feb") */
const formatSeasonDateRange = (season: SeasonModel): string => {
    const ranges = (season.season_date_range || [])
        .filter((r) => r.season_start_date != null && r.season_end_date != null)
        .map((r) => {
            const startM = moment(r.season_start_date)
            const endM = moment(r.season_end_date)
            if (!startM.isValid() || !endM.isValid()) return ""
            return `${startM.format("DD MMM YYYY")} - ${endM.format("DD MMM YYYY")}`
        })
        .filter(Boolean)
    return ranges.join(", ")
}

/* ─────────────────────────── types ─────────────────────────────── */

type PriceLine = { label: string | null; price: number | null }
type RoomOption = { id: string; label: string }

type Props = {
    contracts: HotelContractModel[]
    hotelRoomTypes: HotelRoomTypeModel[]
}

/* ═══════════════════════════ component ═════════════════════════════ */

const RatesOverview: React.FC<Props> = ({ contracts, hotelRoomTypes }) => {
    const [selectedContractId, setSelectedContractId] = useState<string | null>(
        contracts[0]?.contract_id ?? null
    )
    // const [surchargeRoomTab, setSurchargeRoomTab] = useState<string | null>(null)
    // const [standardRoomTab, setStandardRoomTab] = useState<string | null>(null)

    const selectedContract = useMemo(() => {
        if (!selectedContractId) return contracts[0] ?? null
        return contracts.find((c) => c.contract_id === selectedContractId) ?? contracts[0] ?? null
    }, [contracts, selectedContractId])

    const contractOptions = useMemo(
        () =>
            contracts.map((c, i) => ({
                value: c.contract_id || `contract-${i}`,
                label: `${moment(c.contract_start_date).format("DD MMM YYYY")} – ${moment(c.contract_end_date).format("DD MMM YYYY")}`,
            })),
        [contracts]
    )

    /* ── rate structure ── */
    const rateStructureTypeId = selectedContract?.rate_structure_type_id ?? 1
    const rateStructureName = RATE_STRUCTURE_NAMES[rateStructureTypeId] ?? "Simple"
    const isMealPlan = rateStructureTypeId === 2

    /* ── seasons (with date range for header) ── */
    const seasons = useMemo(
        () =>
            (selectedContract?.season || [])
                .filter((s) => s.season_id && s.season_name)
                .map((s) => ({
                    id: s.season_id!,
                    name: s.season_name!,
                    dateRange: formatSeasonDateRange(s),
                })),
        [selectedContract]
    )

    /* ── day-wise groups that opted into the rate table ── */
    /* Mirrors hotelRates.tsx (`dayGroupsInRateTable`): only groups with
     * `flag_enable_day_group_in_rate_table === true` become columns, in `sequence` order. */
    const dayGroupsInRateTable = useMemo(() => {
        const groups: any[] = (selectedContract as any)?.day_wise_pricing ?? []
        return groups
            .filter((g) => g?.flag_enable_day_group_in_rate_table === true)
            .sort((a, b) => {
                const aSeq = typeof a?.sequence === "number" && Number.isFinite(a.sequence) ? a.sequence : 0
                const bSeq = typeof b?.sequence === "number" && Number.isFinite(b.sequence) ? b.sequence : 0
                return (aSeq - bSeq) || String(a?.day_wise_pricing_group_id).localeCompare(String(b?.day_wise_pricing_group_id))
            })
            .map((g) => ({
                id: String(g.day_wise_pricing_group_id),
                name: String(g.day_wise_pricing_group_name || "Day Group"),
            }))
    }, [selectedContract])

    /* ── meal plan types (from contract) ── */
    const mealPlanTypes = useMemo(() => {
        if (!isMealPlan) return []
        return (selectedContract?.meal_plan_type || []).sort((a, b) => a.meal_plan_type_id - b.meal_plan_type_id)
    }, [isMealPlan, selectedContract])

    /* ── room types ── */
    const roomTypes = useMemo(
        () => hotelRoomTypes.filter((rt) => rt.hotel_room_type_id && rt.hotel_room_type_name),
        [hotelRoomTypes]
    )

    /* ── room categorisation for surcharge config ── */
    const hasAnyDifferentSurchargeRoom = useMemo(
        () => hotelRoomTypes.some((rt) => rt.flag_support_different_surcharge === true),
        [hotelRoomTypes]
    )

    const differentRooms = useMemo<RoomOption[]>(
        () =>
            hotelRoomTypes
                .filter((rt) => rt.hotel_room_type_id && rt.flag_support_different_surcharge === true)
                .map((rt) => ({ id: rt.hotel_room_type_id!, label: roomLabel(rt.hotel_room_type_name, rt.bed_type_name) })),
        [hotelRoomTypes]
    )

    const standardRooms = useMemo<RoomOption[]>(
        () =>
            hotelRoomTypes
                .filter((rt) => rt.hotel_room_type_id && rt.flag_support_different_surcharge !== true)
                .map((rt) => ({ id: rt.hotel_room_type_id!, label: roomLabel(rt.hotel_room_type_name, rt.bed_type_name) })),
        [hotelRoomTypes]
    )

    /* ── contract data shorthands ── */
    const pricing = selectedContract?.hotel_room_pricing ?? []
    /* Day-group cells display `surcharge_amount` as a price (same treatment as the Rates tab editor). */
    const dayGroupPricing: any[] = (selectedContract as any)?.hotel_room_day_group_pricing ?? []
    const roomSpecificSurcharges: RoomSpecificDifferentSurchargeModel[] =
        (selectedContract as any)?.room_specific_different_surcharge ?? []
    const mealPlanData = selectedContract?.meal_plan ?? []
    const peakDateSurchargeGroups = selectedContract?.peak_date_surcharge_group ?? []
    const varyByRoomType = selectedContract?.flag_peak_surcharge_vary_by_room_type === true
    const perPersonSurcharges: any[] = (selectedContract as any)?.per_person_seasonal_surcharge ?? []

    const hideAdultExtraBed = shouldHideAdultExtraBedInSurcharge(selectedContract)
    const dayWiseSurchargeRows = useMemo(
        () => getDayWiseSurchargeDisplayRows(selectedContract),
        [selectedContract],
    )
    const seasonalExtraBedRows = useMemo(
        () => getSeasonalExtraBedDisplayRows(selectedContract),
        [selectedContract],
    )
    const dayWiseOverviewRows = useMemo(
        () =>
            dayWiseSurchargeRows.map((d) => ({
                ...d,
                label: d.label,
                value: d.amount,
                isPercentage: d.isPercentage === true,
                day_wise_pricing_group_surcharge: d.day_wise_pricing_group_surcharge,
            })),
        [dayWiseSurchargeRows],
    )

    const surchargeFields = useMemo(() => {
        const fields: {
            key: "child_without_bed_surcharge" | "child_with_bed_surcharge" | "adult_extra_bed_surcharge"
            flagKey:
                | "flag_child_without_bed_surcharge_is_percentage"
                | "flag_child_with_bed_surcharge_is_percentage"
                | "flag_adult_extra_bed_surcharge_is_percentage"
            label: string
        }[] = [
            {
                key: "child_without_bed_surcharge",
                flagKey: "flag_child_without_bed_surcharge_is_percentage",
                label: "Child Without Bed",
            },
            {
                key: "child_with_bed_surcharge",
                flagKey: "flag_child_with_bed_surcharge_is_percentage",
                label: "Child With Bed",
            },
        ]
        if (!hideAdultExtraBed) {
            fields.push({
                key: "adult_extra_bed_surcharge",
                flagKey: "flag_adult_extra_bed_surcharge_is_percentage",
                label: "Adult Extra Bed",
            })
        }
        return fields
    }, [hideAdultExtraBed])

    const surchargeSectionIcon = (
        <IconsColor iconName="dollarIcon" width="18" height="18" strokeColor="#548DE3" />
    )

    /* ── surcharge row lookup ── */
    const getSurchargeRow = (mpTypeId: number | null, flagDiff: boolean) =>
        roomSpecificSurcharges.find(
            (r) => sameMealPlanId(r.meal_plan_type_id, mpTypeId) && r.flag_different_surcharge === flagDiff
        ) ?? null

    /* ════════════════ MAIN RATES cell prices ════════════════ */
    const getCellPrices = (roomTypeId: string, seasonId: string): PriceLine[] => {
        if (rateStructureTypeId === 1) {
            const e = pricing.find(
                (p) => p.hotel_room_type_id === roomTypeId && p.season_id === seasonId && !p.meal_plan_type_id && !p.git_fit_type_id
            )
            return [{ label: null, price: e?.price ?? null }]
        }
        if (rateStructureTypeId === 2) {
            return mealPlanTypes.map((mp) => {
                const e = pricing.find(
                    (p) =>
                        p.hotel_room_type_id === roomTypeId &&
                        p.season_id === seasonId &&
                        p.meal_plan_type_id === mp.meal_plan_type_id &&
                        !p.git_fit_type_id
                )
                return { label: mp.meal_plan_type_name, price: e?.price ?? null }
            })
        }
        if (rateStructureTypeId === 3) {
            const git = pricing.find((p) => p.hotel_room_type_id === roomTypeId && p.season_id === seasonId && p.git_fit_type_id === GitFitType.GIT)
            const fit = pricing.find((p) => p.hotel_room_type_id === roomTypeId && p.season_id === seasonId && p.git_fit_type_id === GitFitType.FIT)
            return [
                { label: "GIT", price: git?.price ?? null },
                { label: "FIT", price: fit?.price ?? null },
            ]
        }
        return []
    }

    /* Day-group cells follow the same 3-structure split as seasons, but join on
     * `day_wise_pricing_group_id` against `hotel_room_day_group_pricing` and read
     * `surcharge_amount` (treated as a price, like the Rates editor does). */
    const getDayGroupCellPrices = (roomTypeId: string, dayGroupId: string): PriceLine[] => {
        const matchGroup = (p: any) => String(p.day_wise_pricing_group_id) === dayGroupId
        if (rateStructureTypeId === 1) {
            const e = dayGroupPricing.find(
                (p) => p.hotel_room_type_id === roomTypeId && matchGroup(p) && !p.meal_plan_type_id && !p.git_fit_type_id
            )
            return [{ label: null, price: e?.surcharge_amount ?? null }]
        }
        if (rateStructureTypeId === 2) {
            return mealPlanTypes.map((mp) => {
                const e = dayGroupPricing.find(
                    (p) =>
                        p.hotel_room_type_id === roomTypeId &&
                        matchGroup(p) &&
                        p.meal_plan_type_id === mp.meal_plan_type_id &&
                        !p.git_fit_type_id
                )
                return { label: mp.meal_plan_type_name, price: e?.surcharge_amount ?? null }
            })
        }
        if (rateStructureTypeId === 3) {
            const git = dayGroupPricing.find((p) => p.hotel_room_type_id === roomTypeId && matchGroup(p) && p.git_fit_type_id === 2)
            const fit = dayGroupPricing.find((p) => p.hotel_room_type_id === roomTypeId && matchGroup(p) && p.git_fit_type_id === 1)
            return [
                { label: "GIT", price: git?.surcharge_amount ?? null },
                { label: "FIT", price: fit?.surcharge_amount ?? null },
            ]
        }
        return []
    }

    /* ════════════════ SHARED UI PIECES ════════════════ */

    const RoomPills = ({ rooms }: { rooms: RoomOption[] }) =>
        rooms.length === 0 ? null : (
            <div className={styles.roomPills}>
                {rooms.map((r) => (
                    <span key={r.id} className={styles.roomPill}>{r.label}</span>
                ))}
            </div>
        )

    /* ── Section wrapper (optional blue header with icon) ── */
    const Section = ({
        title,
        children,
        headerVariant = "default",
        icon,
    }: {
        title: string
        children: React.ReactNode
        headerVariant?: "default" | "blue"
        icon?: React.ReactNode
    }) => (
        <div className={styles.section}>
            <div className={headerVariant === "blue" ? `${styles.sectionTitle} ${styles.sectionTitleBlue}` : styles.sectionTitle}>
                {icon && <span className={styles.sectionTitleIcon}>{icon}</span>}
                {title}
            </div>
            {children}
        </div>
    )

    /* ── Simple two-column info table (label | value) ── */
    const SimpleTable = ({
        rows,
        flagMoreTablesExists = false,
    }: {
        rows: { label: string; value: number | null | undefined; isPercentage?: boolean }[]
        flagMoreTablesExists?: boolean
    }) => (
        <div className={styles.tableWrapper} style={{ padding: flagMoreTablesExists ? "16px 20px" : "0" }}>
            <table className={styles.infoTable}>
                <thead>
                    <tr>
                        <th>Surcharge Type</th>
                        <th className={styles.valueCol}>{getSurchargeAmountColumnHeader(rows)}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.label}>
                            <td>{r.label}</td>
                            <td className={styles.valueCell}>
                                {r.value == null
                                    ? "—"
                                    : `${r.value}${r.isPercentage ? "%" : " VND"}`}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )

    /* ════════════════ SURCHARGE CONFIGURATION ════════════════ */

    const renderExtraBedRateSection = () => {
        if (!seasonalExtraBedRows.length) return null
        return (
            <div className={styles.seasonalExtraBedBlock}>
                <div className={styles.seasonalExtraBedTableTitle}>Extra Bed Rate</div>
                <div className={styles.seasonalExtraBedTableWrap}>
                    <table className={styles.seasonalExtraBedRateTable}>
                        <thead>
                            <tr>
                                <th>Season</th>
                                <th>Extra Bed Rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {seasonalExtraBedRows.map((s) => (
                                <tr key={s.seasonId}>
                                    <td>{s.seasonName}</td>
                                    <td className={styles.valueCell}>
                                        {s.charges == null
                                            ? "—"
                                            : `${s.charges}${s.isPercentage ? "%" : " VND"}`}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        )
    }

    const wrapSurchargeSection = (title: string, body: React.ReactNode | null) => {
        const extraBed = renderExtraBedRateSection()
        if (!body && !extraBed) return null
        return (
            <Section title={title} headerVariant="blue" icon={surchargeSectionIcon}>
                {body}
                {extraBed}
            </Section>
        )
    }

    const renderSurchargeConfiguration = () => {
        /* Case 1 – Simple / GIT-FIT, all rooms same surcharge */
        if (!isMealPlan && !hasAnyDifferentSurchargeRoom) {
            const rows = [
                {
                    label: "Child Without Bed",
                    value: selectedContract?.child_without_bed_price,
                    isPercentage: selectedContract?.flag_child_without_bed_is_percentage === true,
                },
                {
                    label: "Child With Bed",
                    value: selectedContract?.child_with_bed,
                    isPercentage: selectedContract?.flag_child_with_bed_is_percentage === true,
                },
                ...(hideAdultExtraBed
                    ? []
                    : [
                          {
                              label: "Adult Extra Bed",
                              value: selectedContract?.adult_with_bed,
                              isPercentage: selectedContract?.flag_adult_with_bed_is_percentage === true,
                          },
                      ]),
                ...dayWiseOverviewRows,
            ]
            const flagMoreTableExists = hideAdultExtraBed ? rows.length > 2 : false;
            if (!rows.some((r) => r.value != null) && !seasonalExtraBedRows.length) return null
            return wrapSurchargeSection("Surcharge Configuration", <SimpleTable rows={rows} flagMoreTablesExists={flagMoreTableExists} />)
        }

        /* Case 2 – Simple / GIT-FIT, room-specific different surcharges */
        if (!isMealPlan && hasAnyDifferentSurchargeRoom) {
            const renderBlock = (flagDiff: boolean, title: string, rooms: RoomOption[]) => {
                if (!rooms.length) return null
                const row = getSurchargeRow(null, flagDiff);
                const tableRows = [
                    {
                        label: "Child Without Bed",
                        value: row?.child_without_bed_surcharge ?? null,
                        isPercentage: row?.flag_child_without_bed_surcharge_is_percentage === true,
                    },
                    {
                        label: "Child With Bed",
                        value: row?.child_with_bed_surcharge ?? null,
                        isPercentage: row?.flag_child_with_bed_surcharge_is_percentage === true,
                    },
                    ...(hideAdultExtraBed
                        ? []
                        : [
                              {
                                  label: "Adult Extra Bed",
                                  value: row?.adult_extra_bed_surcharge ?? null,
                                  isPercentage: row?.flag_adult_extra_bed_surcharge_is_percentage === true,
                              },
                          ]),
                    ...dayWiseOverviewRows.map((v) => {
                        if (Array.isArray(v.day_wise_pricing_group_surcharge) && v.day_wise_pricing_group_surcharge.length > 0) {
                            const surcharge = v.day_wise_pricing_group_surcharge.find(
                                (s) => s.flag_different_surcharge === flagDiff,
                            )
                            return {
                                label: v.label,
                                value: surcharge ? surcharge.surcharge_amount : null,
                                isPercentage: surcharge?.flag_surcharge_amount_is_percentage === true,
                            }
                        }
                        return v
                    }),
                ]
                const flagMoreTableExists = hideAdultExtraBed ? tableRows.length > 2 : false
                if (!tableRows.some((r) => r.value != null)) return null
                return (
                    <div className={styles.subCard}>
                        <div className={styles.subCardTitle}>{title}</div>
                        <RoomPills rooms={rooms} />
                        <SimpleTable rows={tableRows} flagMoreTablesExists={flagMoreTableExists} />
                    </div>
                )
            }
            const diffBlock = renderBlock(true, "Room-Specific 3rd Person Surcharges (Different Rates)", differentRooms)
            const stdBlock = renderBlock(false, "Standard 3rd Person Surcharges", standardRooms)
            const body =
                diffBlock || stdBlock ? (
                    <div className={`${styles.subCardRow} ${styles.subCardRowStacked}`}>
                        {diffBlock}
                        {stdBlock}
                    </div>
                ) : null
            return wrapSurchargeSection("$ Surcharge Configuration", body)
        }

        /* Case 3 – Meal Plan, all rooms same surcharge */
        if (isMealPlan && !hasAnyDifferentSurchargeRoom) {
            if (!mealPlanTypes.length) return null
            return wrapSurchargeSection("$ Surcharge Configuration", renderMealPlanSurchargeBlock(false, "", []))
        }

        /* Case 4 – Meal Plan, room-specific different surcharges */
        if (isMealPlan && hasAnyDifferentSurchargeRoom) {
            const body = (
                <div className={`${styles.subCardRow} ${styles.subCardRowStacked}`}>
                    {renderMealPlanSurchargeBlock(true, "Room-Specific 3rd Person Surcharges", differentRooms, true)}
                    {renderMealPlanSurchargeBlock(false, "Standard 3rd Person Surcharges", standardRooms, true)}
                </div>
            )
            return wrapSurchargeSection("$ Surcharge Configuration", body)
        }

        return null
    }

    const renderMealPlanSurchargeBlock = (flagDiff: boolean, title: string, rooms: RoomOption[], showTabs?: boolean) => {
        if (!mealPlanTypes.length) return null
        // const activeTab = flagDiff ? (surchargeRoomTab ?? differentRooms[0]?.id) : (standardRoomTab ?? standardRooms[0]?.id)
        // const setActiveTab = flagDiff ? setSurchargeRoomTab : setStandardRoomTab
        const tabRooms = flagDiff ? differentRooms : standardRooms
        return (
            <div className={styles.subCard}>
                {title && <div className={styles.subCardTitle}>{title}</div>}
                {showTabs && tabRooms.length > 0 && (
                    <div className={styles.roomTabsRow}>
                        <div className={styles.roomTabs}>
                            {tabRooms.map((r) => (
                                <button
                                    key={r.id}
                                    type="button"
                                    className={`${styles.roomTab} ${styles.active}`}
                                    // onClick={() => setActiveTab(r.id)}
                                >
                                    {r.label.split(" (")[0]}
                                </button>
                            ))}
                        </div>
                        {flagDiff && (
                            <button type="button" className={styles.differentRatesBtn}>
                                Different Rates
                            </button>
                        )}
                    </div>
                )}
                {!showTabs && rooms.length > 0 && <RoomPills rooms={rooms} />}
                <div className={styles.tableWrapper}>
                    <table className={styles.infoTable}>
                        <thead>
                            <tr>
                                <th>Surcharge Type</th>
                                {mealPlanTypes.map((mp) => (
                                    <th key={mp.meal_plan_type_id} className={styles.valueCol}>
                                        {mp.meal_plan_type_name}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {surchargeFields.map(({ key, flagKey, label }) => (
                                <tr key={key}>
                                    <td>{label}</td>
                                    {mealPlanTypes.map((mp) => {
                                        const row = getSurchargeRow(mp.meal_plan_type_id, flagDiff)
                                        const value = row?.[key]
                                        const isPercentage = row?.[flagKey] === true
                                        return (
                                            <td key={mp.meal_plan_type_id} className={styles.valueCell}>
                                                {value == null ? "—" : `${value}${isPercentage ? "%" : " VND"}`}
                                            </td>
                                        )
                                    })}
                                </tr>
                            ))}
                            {dayWiseSurchargeRows.map((row) => (
                                <tr key={`dw-overview-${row.rowKey}`}>
                                    <td>{row.label}</td>
                                    <td className={styles.valueCell} colSpan={mealPlanTypes.length}>
                                        {row.amount == null
                                            ? "—"
                                            : `${row.amount}${row.isPercentage ? "%" : " VND"}`}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        )
    }

    /* ════════════════ MEAL PLAN ════════════════ */

    const renderMealPlanSection = () => {
        if (!mealPlanData.length) return null
        return (
            <Section
                title="Meal Plan"
                headerVariant="blue"
                icon={<IconsColor iconName="mealIcon" width="18" height="18" strokeColor="#548DE3" />}
            >
                <div className={styles.tableWrapper}>
                    <table className={styles.infoTable}>
                        <thead>
                            <tr>
                                <th>Meal Plan</th>
                                <th className={styles.valueCol}>Adult Charge</th>
                                <th className={styles.valueCol}>Child Charge</th>
                            </tr>
                        </thead>
                        <tbody>
                            {mealPlanData.map((mp, i) => (
                                <tr key={mp.meal_plan_id ?? i}>
                                    <td>{mp.meal_plan_name}</td>
                                    <td className={styles.valueCell}>{formatVND(mp.adult_charge)}</td>
                                    <td className={styles.valueCell}>{formatVND(mp.child_charge)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>
        )
    }

    /* ════════════════ PEAK DATE SURCHARGES ════════════════ */

    const renderPeakDateSurcharges = () => {
        if (!selectedContract?.flag_enable_peak_date_surcharges || !peakDateSurchargeGroups.length) return null

        const allRanges = peakDateSurchargeGroups.flatMap((g) =>
            (g.peak_date_range || [])
                .filter((r) => r.start_date && r.end_date)
                .map((r) => ({
                    label: `${moment(r.start_date).format("DD MMM YYYY")} to ${moment(r.end_date).format("DD MMM YYYY")}`,
                    isDifferent: g.flag_different_surcharge === true,
                }))
        )

        const PeakDatesBlock = () =>
            allRanges.length === 0 ? null : (
                <div className={styles.peakDatesBlock}>
                    <span className={styles.peakDatesLabel}>Applied to Peak Dates:</span>
                    <div className={styles.peakDateChips}>
                        {allRanges.map((r, i) => (
                            <span key={i} className={`${styles.peakDateChip} ${r.isDifferent ? styles.peakDateChipDiff : ""}`}>
                                {r.label}
                                {r.isDifferent && <span className={styles.diffBadge}>Different</span>}
                            </span>
                        ))}
                    </div>
                </div>
            )

        /* Flat (same surcharge for all rooms) */
        if (!varyByRoomType) {
            const stdGroup = peakDateSurchargeGroups.find((g) => g.flag_different_surcharge === false)
            const diffGroup = peakDateSurchargeGroups.find((g) => g.flag_different_surcharge === true)
            return (
                <Section title="Peak Date Surcharges">
                    <PeakDatesBlock />
                    <div className={styles.surchargesRow} style={{ marginTop: 16 }}>
                        {stdGroup?.standard_peak_season_surcharge != null && (
                            <div className={styles.surchargeItem}>
                                <span className={styles.surchargeLabel}>Standard Peak Surcharge (VND / Room / Night)</span>
                                <span className={styles.surchargeValue}>{formatVND(stdGroup.standard_peak_season_surcharge)}</span>
                            </div>
                        )}
                        {diffGroup?.different_peak_season_surcharge != null && (
                            <div className={styles.surchargeItem}>
                                <span className={styles.surchargeLabel}>Different Peak Surcharge (VND / Room / Night)</span>
                                <span className={styles.surchargeValue}>{formatVND(diffGroup.different_peak_season_surcharge)}</span>
                            </div>
                        )}
                    </div>
                </Section>
            )
        }

        /* Room-specific: show one card per room with surcharge value */
        const roomCards = peakDateSurchargeGroups
            .flatMap((pg) =>
                (pg.peak_date_surcharge_room_type_group || [])
                    .filter((g) => (g.peak_date_surcharge_room_type?.length ?? 0) > 0)
                    .flatMap((g) =>
                        (g.peak_date_surcharge_room_type || []).map((rt) => {
                            const meta = hotelRoomTypes.find((h) => h.hotel_room_type_id === rt.hotel_room_type_id)
                            const name = meta?.hotel_room_type_name ?? rt.hotel_room_type_name ?? "Room"
                            return { roomName: name, amount: g.peak_season_surcharge, flag_peak_date_surcharge_is_percentage: g.flag_peak_date_surcharge_is_percentage }
                        })
                    )
            )

        return (
            <Section title="Room-Specific Peak Date Surcharges">
                <PeakDatesBlock />
                <Grid container spacing={2}>
                    {roomCards.map((card, i) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                            <div className={styles.peakRoomCard}>
                                <div className={styles.peakRoomCardName}>{card.roomName}</div>
                                <div className={styles.peakRoomCardValue}>{formatVND(card.amount)}{card.flag_peak_date_surcharge_is_percentage ? "%" : " VND"}</div>
                                <div className={styles.peakRoomCardLabel}>Surcharge ({card.flag_peak_date_surcharge_is_percentage ? "%" : "VND"} / Room / Night)</div>
                            </div>
                        </Grid>
                    ))}
                </Grid>
            </Section>
        )
    }

    /* ════════════════ PER-PERSON SEASONAL SURCHARGES ════════════════ */

    const renderPerPersonSurcharges = () => {
        if (!selectedContract?.flag_enable_per_person_seasonal_surcharge || !perPersonSurcharges.length) return null
        return (
            <Section
                title="Per-Person Seasonal Surcharges"
                headerVariant="blue"
                icon={<IconsColor iconName="personIcon" width="18" height="18" strokeColor="#548DE3" />}
            >
                <div className={styles.tableWrapper}>
                    <table className={styles.infoTable}>
                        <thead>
                            <tr>
                                <th>Season</th>
                                <th className={styles.valueCol}>Adult / Person / Night</th>
                                <th className={styles.valueCol}>Child / Person / Night</th>
                            </tr>
                        </thead>
                        <tbody>
                            {perPersonSurcharges.map((row: any, i: number) => (
                                <tr key={row.season_id ?? i}>
                                    <td>{row.season_name ?? row.season_id}</td>
                                    <td className={styles.valueCell}>{formatVND(row.adult_surcharge)}</td>
                                    <td className={styles.valueCell}>{formatVND(row.child_surcharge)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>
        )
    }

    /* ════════════════ GUARDS ════════════════ */

    if (!selectedContract) {
        return <div className={styles.emptyState}><p>No contracts available. Please create a contract first.</p></div>
    }
    if (roomTypes.length === 0) {
        return <div className={styles.emptyState}><p>No room types found. Please add room types in the Room Categories section first.</p></div>
    }
    // A contract may skip seasons when its day groups are shown in the rate table
    if (seasons.length === 0 && dayGroupsInRateTable.length === 0) {
        return <div className={styles.emptyState}><p>No seasons configured. Please configure seasons in the contract settings first.</p></div>
    }

    /* ════════════════ RENDER ════════════════ */

    return (
        <div className={styles.container}>

            {/* Contract Selector */}
            {/* {contracts.length > 1 && (
                <div className={styles.contractSelectorRow}>
                    <span className={styles.contractSelectorLabel}>Contract</span>
                    <Select
                        label=""
                        value={selectedContract.contract_id || ""}
                        onChange={(e) => setSelectedContractId(e.target.value)}
                        options={contractOptions}
                        width="340px"
                    />
                </div>
            )} */}

            {/* Overview info cards (4 cards with icons) */}
            <div className={styles.overviewCards}>
                <div className={styles.overviewCard}>
                    <div className={styles.overviewCardIcon}>
                        <IconsColor iconName="calendarIcon" width="22" height="22" strokeColor="#1a3a5c" />
                    </div>
                    <div className={styles.overviewCardContent}>
                        {/* <div className={styles.overviewCardLabel}>Contract Period</div> */}
                        <div className={styles.overviewCardValue}>
                        {contracts.length > 1 ? (
                <div className={styles.contractSelectorRow}>
                    <Select
                        label=""
                        value={selectedContract.contract_id || ""}
                        onChange={(e) => setSelectedContractId(e.target.value)}
                        options={contractOptions}
                        width="340px"
                    />
                </div>
            ) : (
                <>
                <div className={styles.overviewCardLabel}>Contract Period</div>
                <div className={styles.overviewCardValue}>
                    {moment(selectedContract.contract_start_date).format("DD MMM YYYY")} to{" "}
                    {moment(selectedContract.contract_end_date).format("DD MMM YYYY")}
                </div>
                </>
            )}
                        </div>
                    </div>
                </div>
                <div className={styles.overviewCard}>
                    <div className={styles.overviewCardIcon}>
                        <IconsColor iconName="gearIcon" width="22" height="22" strokeColor="#1a3a5c" />
                    </div>
                    <div className={styles.overviewCardContent}>
                        <div className={styles.overviewCardLabel}>Rate Structure</div>
                        <div className={styles.overviewCardValue}>{rateStructureName}</div>
                    </div>
                </div>
                <div className={styles.overviewCard}>
                    <div className={styles.overviewCardIcon}>
                        <IconsColor iconName="personIcon" width="22" height="22" strokeColor="#1a3a5c" />
                    </div>
                    <div className={styles.overviewCardContent}>
                        <div className={styles.overviewCardLabel}>Free Child Age</div>
                        <div className={styles.overviewCardValue}>
                            {selectedContract.free_child_age_limit != null
                                ? `0 – ${selectedContract.free_child_age_limit} yrs`
                                : "—"}
                        </div>
                    </div>
                </div>
                <div className={styles.overviewCard}>
                    <div className={styles.overviewCardIcon}>
                        <IconsColor iconName="dollarIcon" width="22" height="22" strokeColor="#1a3a5c" />
                    </div>
                    <div className={styles.overviewCardContent}>
                        <div className={styles.overviewCardLabel}>Chargeable Child Age</div>
                        <div className={styles.overviewCardValue}>
                            {selectedContract.chargeable_child_age_limit != null
                                ? `${(selectedContract.free_child_age_limit ?? 0) + 1} – ${selectedContract.chargeable_child_age_limit} yrs`
                                : "—"}
                        </div>
                    </div>
                </div>
            </div>

            {/* ① Seasonal Room Rates – always full width */}
            <Section title="Seasonal Room Rates">
                <div className={styles.tableWrapper}>
                    <table className={styles.ratesTable}>
                        <thead>
                            <tr>
                                <th className={styles.roomCategoryTh}>Room Category</th>
                                {seasons.map((s) => (
                                    <th key={`season-${s.id}`} className={styles.seasonTh}>
                                        <div className={styles.seasonThName}>{toTitleCase(s.name)}</div>
                                        {s.dateRange ? (
                                            <div className={styles.seasonThDateRange}>{s.dateRange}</div>
                                        ) : null}
                                    </th>
                                ))}
                                {dayGroupsInRateTable.map((g) => (
                                    <th key={`daygroup-${g.id}`} className={styles.seasonTh}>
                                        <div className={styles.seasonThName}>{g.name}</div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {roomTypes.map((rt, rowIdx) => {
                                const meta = [
                                    rt.bed_type_name,
                                    rt.maximum_occupancy != null ? `Max Occ: ${rt.maximum_occupancy}` : null,
                                ].filter(Boolean).join(" • ")

                                return (
                                    <tr key={rt.hotel_room_type_id} className={rowIdx % 2 === 1 ? styles.altRow : ""}>
                                        <td className={styles.roomCategoryTd}>
                                            <div className={styles.roomName}>{toTitleCase(rt.hotel_room_type_name)}</div>
                                            {meta && <div className={styles.roomMeta}>{toTitleCase(meta)}</div>}
                                        </td>
                                        {seasons.map((s) => {
                                            const lines = getCellPrices(rt.hotel_room_type_id!, s.id)
                                            return (
                                                <td key={`season-${s.id}`} className={styles.priceTd}>
                                                    {lines.map((line, li) => (
                                                        <div key={li} className={styles.priceLine}>
                                                            {line.label && (
                                                                <span className={styles.priceLineLabel}>{line.label}:&nbsp;</span>
                                                            )}
                                                            <span className={line.price !== null ? styles.priceValue : styles.emptyPrice}>
                                                                {formatVND(line.price)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </td>
                                            )
                                        })}
                                        {dayGroupsInRateTable.map((g) => {
                                            const lines = getDayGroupCellPrices(rt.hotel_room_type_id!, g.id)
                                            return (
                                                <td key={`daygroup-${g.id}`} className={styles.priceTd}>
                                                    {lines.map((line, li) => (
                                                        <div key={li} className={styles.priceLine}>
                                                            {line.label && (
                                                                <span className={styles.priceLineLabel}>{line.label}:&nbsp;</span>
                                                            )}
                                                            <span className={line.price !== null ? styles.priceValue : styles.emptyPrice}>
                                                                {formatVND(line.price)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </Section>

            {/* MUI Grid: Surcharge (left ~2/3) | Meal Plan + Per-Person (right ~1/3) */}
            <Grid container spacing={2} alignItems="stretch">
                <Grid size={{ xs: 12, md: 6 }}>
                    {renderSurchargeConfiguration()}
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <div className={styles.gridRightCol}>
                        {renderMealPlanSection()}
                        {renderPerPersonSurcharges()}
                    </div>
                </Grid>
            </Grid>

            {/* ④ Peak Date Surcharges (full width bottom) */}
            {renderPeakDateSurcharges()}

        </div>
    )
}

export default RatesOverview
